<template>
  <el-form 
    :model="form" 
    :rules="rules" 
    ref="formRef" 
    label-position="top" 
    class="trip-form"
  >
    <el-row :gutter="20">
      <!-- 起点城市 -->
      <el-col :xs="24" :sm="12">
        <el-form-item label="起点城市" prop="departureCity">
          <el-input 
            v-model="form.departureCity" 
            placeholder="请输入起点城市，如：北京"
            size="large"
            :prefix-icon="Location"
          />
        </el-form-item>
      </el-col>

      <!-- 目标城市 -->
      <el-col :xs="24" :sm="12">
        <el-form-item label="目标城市" prop="destinationCity">
          <el-input 
            v-model="form.destinationCity" 
            placeholder="请输入目标城市，如：成都"
            size="large"
            :prefix-icon="Flag"
          />
        </el-form-item>
      </el-col>

      <!-- 出行月份 -->
      <el-col :xs="24" :sm="8">
        <el-form-item label="出行月份" prop="month">
          <el-select 
            v-model="form.month" 
            placeholder="选择月份"
            size="large"
            style="width: 100%"
          >
            <el-option
              v-for="month in 12"
              :key="month"
              :label="month + '月'"
              :value="month"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <!-- 出行天数 -->
      <el-col :xs="24" :sm="8">
        <el-form-item label="出行天数" prop="days">
          <el-select 
            v-model="form.days" 
            placeholder="选择天数"
            size="large"
            style="width: 100%"
          >
            <el-option
              v-for="day in 15"
              :key="day"
              :label="day + '天'"
              :value="day"
            />
          </el-select>
        </el-form-item>
      </el-col>

      <!-- 行程类型 -->
      <el-col :xs="24" :sm="8">
        <el-form-item label="行程类型" prop="tripType">
          <el-radio-group v-model="form.tripType" size="large">
            <el-radio-button label="往返">往返</el-radio-button>
            <el-radio-button label="单程">单程</el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-col>

      <!-- 成人人数 -->
      <el-col :xs="12" :sm="6">
        <el-form-item label="成人" prop="people.adult">
          <el-input-number 
            v-model="form.people.adult" 
            :min="1" 
            :max="10"
            size="large"
            style="width: 100%"
          />
        </el-form-item>
      </el-col>

      <!-- 儿童人数 -->
      <el-col :xs="12" :sm="6">
        <el-form-item label="儿童" prop="people.child">
          <el-input-number 
            v-model="form.people.child" 
            :min="0" 
            :max="5"
            size="large"
            style="width: 100%"
          />
        </el-form-item>
      </el-col>

      <!-- 孩子年龄（仅当有儿童时显示） -->
      <el-col :xs="12" :sm="6" v-if="form.people.child > 0">
        <el-form-item label="孩子年龄" prop="kidAge">
          <el-input-number 
            v-model="form.kidAge" 
            :min="0" 
            :max="18"
            size="large"
            style="width: 100%"
            placeholder="主要孩子的年龄"
          />
        </el-form-item>
      </el-col>

      <!-- 车型 -->
      <el-col :xs="24" :sm="6">
        <el-form-item label="车型" prop="car">
          <el-select 
            v-model="form.car" 
            placeholder="选择车型"
            size="large"
            style="width: 100%"
          >
            <el-option label="SUV（12L/100km）" value="SUV" />
            <el-option label="越野车（14L/100km）" value="越野" />
            <el-option label="轿车（10L/100km）" value="轿车" />
            <el-option label="MPV（13L/100km）" value="MPV" />
          </el-select>
        </el-form-item>
      </el-col>

      <!-- 预算上限 -->
      <el-col :xs="24" :sm="12">
        <el-form-item label="预算上限（可选）" prop="budget">
          <el-input-number 
            v-model="form.budget" 
            :min="1000" 
            :max="50000"
            :step="500"
            size="large"
            style="width: 100%"
            placeholder="不限"
          >
            <template #suffix>元</template>
          </el-input-number>
        </el-form-item>
      </el-col>

      <!-- 主题偏好 -->
      <el-col :xs="24">
        <el-form-item label="主题偏好" prop="theme">
          <el-radio-group v-model="form.theme" size="large">
            <el-radio-button label="亲子">
              <el-icon><HomeFilled /></el-icon> 亲子
            </el-radio-button>
            <el-radio-button label="情侣">
              <el-icon><StarFilled /></el-icon> 情侣
            </el-radio-button>
            <el-radio-button label="摄影">
              <el-icon><CameraFilled /></el-icon> 摄影
            </el-radio-button>
            <el-radio-button label="美食">
              <el-icon><Food /></el-icon> 美食
            </el-radio-button>
            <el-radio-button label="山水">
              <el-icon><Place /></el-icon> 山水
            </el-radio-button>
            <el-radio-button label="越野">
              <el-icon><Promotion /></el-icon> 越野/非铺装
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-col>
    </el-row>

    <!-- 提交按钮 -->
    <el-form-item class="submit-item">
      <el-button 
        type="primary" 
        size="large" 
        @click="handleSubmit"
        :loading="loading"
        class="submit-btn"
      >
        <el-icon class="btn-icon"><Compass /></el-icon>
        {{ loading ? '正在生成中...' : '生成路线' }}
      </el-button>
    </el-form-item>
  </el-form>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { Location, Compass, HomeFilled, StarFilled, CameraFilled, Food, Place, Flag, Promotion } from '@element-plus/icons-vue'

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit'])

const formRef = ref(null)

const form = reactive({
  departureCity: '北京',
  destinationCity: '',
  month: new Date().getMonth() + 1,
  days: 3,
  tripType: '往返',
  people: {
    adult: 2,
    child: 0
  },
  kidAge: 10,
  car: 'SUV',
  budget: null,
  theme: '亲子'
})

const rules = {
  departureCity: [
    { required: true, message: '请输入起点城市', trigger: 'blur' },
    { min: 2, max: 20, message: '城市名称长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  destinationCity: [
    { required: true, message: '请输入目标城市', trigger: 'blur' },
    { min: 2, max: 20, message: '城市名称长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  month: [
    { required: true, message: '请选择出行月份', trigger: 'change' }
  ],
  days: [
    { required: true, message: '请选择出行天数', trigger: 'change' }
  ],
  'people.adult': [
    { required: true, message: '请输入成人数', trigger: 'change' }
  ],
  car: [
    { required: true, message: '请选择车型', trigger: 'change' }
  ],
  theme: [
    { required: true, message: '请选择主题偏好', trigger: 'change' }
  ]
}

const handleSubmit = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate((valid) => {
    if (valid) {
      emit('submit', { ...form })
    }
  })
}
</script>

<style scoped>
.trip-form {
  padding: 20px 0;
}

.submit-item {
  margin-top: 30px;
  text-align: center;
}

.submit-btn {
  width: 200px;
  height: 50px;
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
  border: none;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 181, 255, 0.4);
}

.btn-icon {
  margin-right: 8px;
  font-size: 20px;
}

:deep(.el-form-item__label) {
  font-weight: 600;
  color: #333;
  font-size: 15px;
}

:deep(.el-input__inner) {
  border-radius: 10px;
}

:deep(.el-radio-button__inner) {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 12px 20px;
}
</style>